/**
 * Support → customer record (D-129): identity and contact details (changed with a log), the
 * Academy access with the capped gestures (resend the access e-mail, extend, oral credits,
 * trainer review, unlock), the exchanges, the immigration files WITHOUT their documents, the
 * invoices and payments read-only (send again), and the customer's requests.
 */

import "./support.css";

import {
  type SupportCustomerCard,
  useSupportCustomersDetailsCreate,
  useSupportCustomersResendDocumentCreate,
  useSupportCustomersRetrieve,
  useSupportLearnersCreditsCreate,
  useSupportLearnersDetailsCreate,
  useSupportLearnersExtendCreate,
  useSupportLearnersResendAccessCreate,
  useSupportLearnersRetrieve,
  useSupportLearnersTrainerReviewCreate,
  useSupportLearnersUnblockCreate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField, Skeleton, TextField } from "@agencyos/ui-web";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import { dateText, dateTimeText } from "../billing/shared";
import { useRefreshSupport } from "./shared";

interface Identity {
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  language?: string;
  city?: string;
  country?: string;
  created_at?: string | null;
}
interface Access {
  kind: string;
  status: string;
  ends_on: string | null;
  exams: string[];
}
interface LearnerBlock {
  id: string;
  access: Access | null;
  oral_credits: { remaining: number; resets_on: string | null };
  trainer_review: { remaining: number; included: number };
  productions: { id: string; exam: string; skill: string; created_at: string; reviewable: boolean }[];
  subscriptions: { id: string; kind: string; status: string; amount_xaf: number; refundable: boolean; ends_on: string | null }[];
}
interface Caps {
  extend_max_days?: number;
  oral_credits_max?: number;
  capped?: boolean;
}

function useTicketRef(): { ticket?: string } {
  const [params] = useSearchParams();
  const ticket = params.get("ticket");
  return ticket ? { ticket } : {};
}

function IdentityCard({ card, kind, id }: { card: SupportCustomerCard; kind: "customers" | "learners"; id: string }) {
  const { t, i18n } = useTranslation();
  const ref = useTicketRef();
  const refresh = useRefreshSupport();
  const identity = card.identity as Identity;
  const can = card.can as Record<string, boolean>;
  const contactDetails = useSupportCustomersDetailsCreate({ mutation: { onSuccess: refresh } });
  const learnerDetails = useSupportLearnersDetailsCreate({ mutation: { onSuccess: refresh } });
  const save = kind === "customers" ? contactDetails : learnerDetails;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: identity.first_name ?? "",
    last_name: identity.last_name ?? "",
    email: identity.email ?? "",
    phone_e164: identity.phone ?? "",
    language: identity.language === "en" ? "en" : "fr",
    city: identity.city ?? "",
  });
  const errors = fieldErrors(save.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const data = { ...form, language: form.language as "fr" | "en", ...ref };
    const done = { onSuccess: () => setEditing(false) };
    if (kind === "customers") contactDetails.mutate({ id, data }, done);
    else learnerDetails.mutate({ id, data }, done);
  };
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-identity-title">
      <h2 id="support-identity-title">{t("support.card.identity")}</h2>
      {!editing ? (
        <dl className="support-facts">
          <dt>{t("support.card.name")}</dt>
          <dd>{identity.name || "—"}</dd>
          <dt>{t("support.card.email")}</dt>
          <dd>{identity.email || "—"}</dd>
          <dt>{t("support.card.phone")}</dt>
          <dd>{identity.phone || "—"}</dd>
          <dt>{t("support.card.language")}</dt>
          <dd>{identity.language || "—"}</dd>
          <dt>{t("support.card.city")}</dt>
          <dd>{[identity.city, identity.country].filter(Boolean).join(", ") || "—"}</dd>
        </dl>
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("support.card.edit")}>
          <div className="form-grid">
            <TextField label={t("support.card.firstName")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} disabled={kind === "learners" && !card.identity.contact_id} />
            <TextField label={t("support.card.lastName")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} disabled={kind === "learners" && !card.identity.contact_id} />
            <TextField type="email" label={t("support.card.email")} value={form.email} error={errors.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField type="tel" label={t("support.card.phone")} value={form.phone_e164} error={errors.phone_e164} onChange={(e) => setForm({ ...form, phone_e164: e.target.value })} />
            <SelectField label={t("support.card.language")} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} options={[{ value: "fr", label: "Français" }, { value: "en", label: "English" }]} />
            <TextField label={t("support.card.city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="row">
            <Button type="submit" disabled={save.isPending}>
              {t("support.card.saveDetails")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              {t("support.new.cancel")}
            </Button>
          </div>
        </form>
      )}
      {identity.created_at ? <p className="muted">{t("support.card.since", { date: dateText(identity.created_at, i18n.language) })}</p> : null}
      {can.edit && !editing ? (
        <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
          {t("support.card.edit")}
        </Button>
      ) : null}
      {save.isSuccess && !editing ? <span role="status">{t("support.card.saved")}</span> : null}
    </GlassCard>
  );
}

function Gesture({
  title,
  label,
  max,
  pending,
  error,
  onApply,
}: {
  title: string;
  label: string;
  max?: number;
  pending: boolean;
  error?: string;
  onApply: (value: number, note: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  return (
    <form
      className="stack support-gesture"
      aria-label={title}
      onSubmit={(e) => {
        e.preventDefault();
        onApply(Number(value), note.trim());
      }}
    >
      <h3>{title}</h3>
      <div className="form-grid">
        <TextField type="number" min={1} max={max} label={label} value={value} error={error} onChange={(e) => setValue(e.target.value)} />
        <TextField label={t("support.card.reason")} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <Button type="submit" variant="secondary" disabled={pending || !value || !note.trim() || (max !== undefined && Number(value) > max)}>
        {t("support.card.apply")}
      </Button>
    </form>
  );
}

function AcademyCard({ card }: { card: SupportCustomerCard }) {
  const { t, i18n } = useTranslation();
  const ref = useTicketRef();
  const refresh = useRefreshSupport();
  const learner = card.learner as LearnerBlock | null;
  const can = card.can as Record<string, boolean>;
  const caps = card.caps as Caps;
  const options = { mutation: { onSuccess: refresh } };
  const resend = useSupportLearnersResendAccessCreate(options);
  const extend = useSupportLearnersExtendCreate(options);
  const credits = useSupportLearnersCreditsCreate(options);
  const review = useSupportLearnersTrainerReviewCreate(options);
  const unblock = useSupportLearnersUnblockCreate(options);
  if (!learner) return null;
  const access = learner.access;
  const extendMax = caps.capped ? caps.extend_max_days : undefined;
  const creditsMax = caps.capped ? caps.oral_credits_max : undefined;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-academy-title">
      <h2 id="support-academy-title">{t("support.card.academy")}</h2>
      <p>
        <strong>{t("support.card.access")}</strong> :{" "}
        {access ? (
          <>
            <Badge tone={access.status === "active" ? "success" : "warning"}>{t(`access.status.${access.status}`, { defaultValue: access.status })}</Badge>{" "}
            {t(`access.kind.${access.kind}`, { defaultValue: access.kind })}{" "}
            {access.ends_on ? t("support.card.until", { date: dateText(access.ends_on, i18n.language) }) : null} ·{" "}
            {access.exams.map((exam) => t(`access.examShort.${exam}`, { defaultValue: exam })).join(", ")}
          </>
        ) : (
          <span className="muted">{t("support.card.noAccess")}</span>
        )}
      </p>
      <p className="muted">
        {t("support.card.credits", { remaining: learner.oral_credits.remaining, date: learner.oral_credits.resets_on ? dateText(learner.oral_credits.resets_on, i18n.language) : "—" })}
        <br />
        {t("support.card.reviews", { remaining: learner.trainer_review.remaining, included: learner.trainer_review.included })}
      </p>
      {can.actions ? (
        <>
          <div className="row">
            <Button type="button" variant="secondary" disabled={resend.isPending} onClick={() => resend.mutate({ id: learner.id, data: ref })}>
              {t("support.card.resendAccess")}
            </Button>
            {resend.isSuccess ? <span role="status">{t("support.card.resent")}</span> : null}
            <Button type="button" variant="secondary" disabled={unblock.isPending} onClick={() => unblock.mutate({ id: learner.id, data: ref })}>
              {t("support.card.unblock")}
            </Button>
            {unblock.isSuccess ? <span role="status">{t("support.card.unblocked")}</span> : null}
          </div>
          <p className="muted">{t("support.card.unblockHint")}</p>
          <div className="grid-2">
            <Gesture
              title={t("support.card.extend")}
              label={extendMax ? t("support.card.extendDays", { max: extendMax }) : t("support.card.extendDaysFree")}
              max={extendMax}
              pending={extend.isPending}
              error={fieldErrors(extend.error).days ?? fieldErrors(extend.error).detail}
              onApply={(days, note) => extend.mutate({ id: learner.id, data: { days, note, ...ref } })}
            />
            <Gesture
              title={t("support.card.addCredits")}
              label={creditsMax ? t("support.card.creditsAmount", { max: creditsMax }) : t("support.card.creditsAmountFree")}
              max={creditsMax}
              pending={credits.isPending}
              error={fieldErrors(credits.error).amount}
              onApply={(amount, note) => credits.mutate({ id: learner.id, data: { amount, note, ...ref } })}
            />
          </div>
          {extend.isSuccess || credits.isSuccess ? <span role="status">{t("support.card.done")}</span> : null}
        </>
      ) : null}
      {learner.productions.length ? (
        <section className="stack" aria-labelledby="support-productions-title">
          <h3 id="support-productions-title">{t("support.card.productions")}</h3>
          <ul className="plain-list stack">
            {learner.productions.map((p) => (
              <li key={p.id} className="row between">
                <span>
                  {t(`access.examShort.${p.exam}`, { defaultValue: p.exam })} · {p.skill.toUpperCase()} · {dateTimeText(p.created_at, i18n.language)}
                </span>
                {can.actions && p.reviewable ? (
                  <Button type="button" size="sm" variant="ghost" disabled={review.isPending} onClick={() => review.mutate({ id: learner.id, data: { production: p.id, ...ref } })}>
                    {t("support.card.askReview")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {review.isSuccess ? <span role="status">{t("support.card.reviewAsked")}</span> : null}
          {fieldErrors(review.error).production ? <p role="alert">{fieldErrors(review.error).production}</p> : null}
        </section>
      ) : null}
    </GlassCard>
  );
}

function MoneyCard({ card, contactId }: { card: SupportCustomerCard; contactId: string }) {
  const { t, i18n } = useTranslation();
  const ref = useTicketRef();
  const resend = useSupportCustomersResendDocumentCreate();
  const can = card.can as Record<string, boolean>;
  const invoices = card.invoices as { id: string; number: string; status: string; total_ttc_xaf: number; paid_xaf: number; due_date: string | null }[];
  const payments = card.payments as { id: string; invoice: string; amount_xaf: number; method: string; status: string; receipt_number: string; confirmed_at: string | null }[];
  if (!invoices.length && !payments.length) return null;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="support-money-title">
      <h2 id="support-money-title">{t("support.card.invoices")}</h2>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {invoices.map((row) => (
              <tr key={row.id}>
                <td>{row.number || "—"}</td>
                <td>{t(`billing.status.${row.status}`, { defaultValue: row.status })}</td>
                <td>{formatXaf(row.total_ttc_xaf)}</td>
                <td>{formatXaf(row.paid_xaf)}</td>
                <td>{dateText(row.due_date, i18n.language)}</td>
                <td>
                  {can.actions && contactId && row.status !== "draft" ? (
                    <Button size="sm" variant="ghost" onClick={() => resend.mutate({ id: contactId, data: { invoice: row.id, ...ref } })}>
                      {t("support.card.resendInvoice")}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>{t("support.card.payments")}</h3>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {payments.map((row) => (
              <tr key={row.id}>
                <td>{row.invoice}</td>
                <td>{formatXaf(row.amount_xaf)}</td>
                <td>{t(`billing.method.${row.method}`, { defaultValue: row.method })}</td>
                <td>{t(`billing.paymentStatus.${row.status}`, { defaultValue: row.status })}</td>
                <td>{row.receipt_number || "—"}</td>
                <td>
                  {can.actions && contactId && row.status === "confirmed" ? (
                    <Button size="sm" variant="ghost" onClick={() => resend.mutate({ id: contactId, data: { payment: row.id, ...ref } })}>
                      {t("support.card.resendReceipt")}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {resend.isSuccess ? <span role="status">{t("support.card.documentSent")}</span> : null}
    </GlassCard>
  );
}

function ContextCard({ card }: { card: SupportCustomerCard }) {
  const { t, i18n } = useTranslation();
  const exchanges = card.exchanges as { channel: string; direction: string; body: string; masked: boolean; created_at: string }[];
  const dossiers = card.dossiers as { id: string; reference: string; status_label: string; items: { title: string; status_label: string; has_file: boolean }[] }[];
  const tickets = card.tickets as { id: string; number: string; subject: string; status: string; created_at: string }[];
  return (
    <>
      {tickets.length ? (
        <GlassCard as="section" className="stack" aria-labelledby="support-card-tickets">
          <h2 id="support-card-tickets">{t("support.card.tickets")}</h2>
          <ul className="plain-list stack">
            {tickets.map((row) => (
              <li key={row.id}>
                <Link to={`/support/tickets/${row.id}`}>{row.number}</Link> · {row.subject} · <Badge>{t(`support.status.${row.status}`)}</Badge>
              </li>
            ))}
          </ul>
        </GlassCard>
      ) : null}
      {dossiers.length ? (
        <GlassCard as="section" className="stack" aria-labelledby="support-card-dossiers">
          <h2 id="support-card-dossiers">{t("support.card.dossiers")}</h2>
          <p className="muted">{t("support.card.documentsMasked")}</p>
          {dossiers.map((dossier) => (
            <details key={dossier.id}>
              <summary>
                {dossier.reference} · {dossier.status_label}
              </summary>
              <ul className="plain-list">
                {dossier.items.map((item, index) => (
                  <li key={index}>
                    {item.title} — {item.status_label}
                    {item.has_file ? <small className="muted"> · {t("support.card.masked")}</small> : null}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </GlassCard>
      ) : null}
      {exchanges.length ? (
        <GlassCard as="section" className="stack" aria-labelledby="support-card-exchanges">
          <h2 id="support-card-exchanges">{t("support.card.exchanges")}</h2>
          <ol className="plain-list support-thread">
            {exchanges.map((m, index) => (
              <li key={index} className={`bubble bubble--${m.direction}`}>
                <p className="bubble__meta">
                  {m.channel} · {dateTimeText(m.created_at, i18n.language)}
                </p>
                <p className="bubble__body">{m.masked ? <em>{t("support.card.masked")}</em> : m.body}</p>
              </li>
            ))}
          </ol>
        </GlassCard>
      ) : null}
    </>
  );
}

export function CustomerCardPage({ kind }: { kind: "customers" | "learners" }) {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const customer = useSupportCustomersRetrieve(id, { query: { enabled: kind === "customers" } });
  const learner = useSupportLearnersRetrieve(id, { query: { enabled: kind === "learners" } });
  const query = kind === "customers" ? customer : learner;
  const card = query.data?.status === 200 ? query.data.data : undefined;
  if (!card) {
    return <Page title={t("support.card.title")}>{query.isPending ? <Skeleton style={{ height: 300 }} /> : <p role="alert">{t("common.error")}</p>}</Page>;
  }
  const identity = card.identity as Identity & { contact_id?: string };
  return (
    <Page eyebrow={<Link to="/support?tab=customers">{t("support.ticket.back")}</Link>} title={identity.name || identity.email || t("support.card.title")} description={t("support.card.title")}>
      <div className="detail-layout">
        <div className="detail-layout__main stack">
          <AcademyCard card={card} />
          <MoneyCard card={card} contactId={identity.contact_id ?? ""} />
          <ContextCard card={card} />
        </div>
        <aside className="detail-layout__aside stack">
          <IdentityCard card={card} kind={kind} id={id} />
        </aside>
      </div>
    </Page>
  );
}
